import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceCollaboratorsQueryKey } from '@/utils/queryKeys';
import type {
  RemoveExperienceOrganizationPermissionMutation,
  RemoveExperienceOrganizationPermissionMutationVariables,
} from './useRemoveExperienceOrganizationPermission.generated';

const query = gql`
  mutation RemoveExperienceOrganizationPermission(
    $input: RemoveExperienceOrganizationPermissionInput!
  ) {
    removeExperienceOrganizationPermission(input: $input)
  }
`;

export const useRemoveExperienceOrganizationPermission = () => {
  const queryClient = useQueryClient();
  const experience = useEffectiveExperience();
  const experienceId = String(experience.id);

  const { mutate, mutateAsync, error, isPending, reset } = useMutation<
    RemoveExperienceOrganizationPermissionMutation,
    QueryError,
    RemoveExperienceOrganizationPermissionMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        RemoveExperienceOrganizationPermissionMutation,
        RemoveExperienceOrganizationPermissionMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceCollaboratorsQueryKey(experienceId),
      });
    },
  });

  const removeOrganizationPermission = (
    organizationId: string,
    options?: MutateOptions<
      RemoveExperienceOrganizationPermissionMutation,
      QueryError,
      RemoveExperienceOrganizationPermissionMutationVariables
    >
  ) => mutate({ input: { experienceId, organizationId } }, options);

  // Promise-returning variant for callers that aggregate failures across
  // several removals — consecutive `mutate` calls only fire the callbacks of
  // the latest one.
  const removeOrganizationPermissionAsync = (organizationId: string) =>
    mutateAsync({ input: { experienceId, organizationId } });

  return {
    removeOrganizationPermission,
    removeOrganizationPermissionAsync,
    error,
    isPending,
    reset,
  };
};
